import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { mockData } from '../mockData';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';

const TestimonialsSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center', slidesToScroll: 1 },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" className="md:py-20 py-5 bg-navy-900 relative overflow-hidden">
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-gold-400/5 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-navy-500/10 rounded-full filter blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
            What Our{' '}
            <span className="bg-brand-gradient bg-clip-text text-transparent">Clients Say</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Real stories from founders and brand managers who chose us
          </p>
        </div>

        <div className="relative px-14">
          {/* Arrows */}
          <Button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-navy-700 hover:bg-gold-400 hover:text-navy-900 text-gold-400 rounded-full w-10 h-10 p-0 shadow-lg z-20 border border-gold-400/40"
            size="icon"
            data-no-hover="true"
          >
            <ChevronLeft size={20} />
          </Button>
          <Button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-navy-700 hover:bg-gold-400 hover:text-navy-900 text-gold-400 rounded-full w-10 h-10 p-0 shadow-lg z-20 border border-gold-400/40"
            size="icon"
            data-no-hover="true"
          >
            <ChevronRight size={20} />
          </Button>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {mockData.testimonials.map((testimonial, index) => {
                const isCenter = index === selectedIndex;
                return (
                  <div
                    key={testimonial.id}
                    className="flex-[0_0_90%] md:flex-[0_0_45%] lg:flex-[0_0_38%] min-w-0 px-3 transition-all duration-500"
                    style={{
                      filter: isCenter ? 'none' : 'blur(2px)',
                      opacity: isCenter ? 1 : 0.4,
                      transform: isCenter ? 'scale(1)' : 'scale(0.92)',
                      transition: 'filter 0.5s ease, opacity 0.5s ease, transform 0.5s ease'
                    }}
                  >
                    <div className={`rounded-2xl p-7 shadow-lg relative h-full border transition-all duration-500 ${
                      isCenter
                        ? 'bg-navy-700 border-gold-400/40 shadow-gold-400/10 shadow-2xl'
                        : 'bg-navy-800 border-navy-600/30'
                    }`}>
                      {/* Quote icon */}
                      <div className={`absolute top-5 right-5 p-2.5 rounded-full transition-opacity ${isCenter ? 'opacity-30' : 'opacity-10'}`}>
                        <Quote size={22} className="text-gold-400" />
                      </div>

                      {/* Profile */}
                      <div className="flex items-center space-x-4 mb-5">
                        <div className="relative">
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className={`w-14 h-14 rounded-full object-cover border-2 transition-all ${
                              isCenter ? 'border-gold-400' : 'border-navy-500'
                            }`}
                          />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-100 text-base">{testimonial.name}</h4>
                          <p className="text-gray-400 text-xs">{testimonial.role}</p>
                        </div>
                      </div>

                      {/* Text */}
                      <p className="text-gray-200 text-sm leading-relaxed italic mb-4">"{testimonial.text}"</p>

                      {/* Stars */}
                      <div className="flex space-x-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={14} className="text-gold-400 fill-gold-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {mockData.testimonials.map((_, index) => (
            <button
              key={index}
              data-no-hover="true"
              className={`h-2 rounded-full transition-all duration-300 ${
                index === selectedIndex ? 'bg-gold-400 w-6' : 'bg-navy-600 w-2 hover:bg-gold-400/50'
              }`}
              onClick={() => emblaApi && emblaApi.scrollTo(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSlider;
